<template>
  <div class="dark-theme-wrapper">
    <div class="container py-5">
      <h1 class="main-title text-center mb-5">SPRÁVA TÝMŮ</h1>

      <div class="admin-card mx-auto mb-5">
        <h2 class="section-title text-center mb-4">
          {{ editing ? "UPRAVIT TÝM" : "PŘIDAT NOVÝ TÝM" }}
        </h2>

        <form @submit.prevent="editing ? updateTeam() : addTeam()">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="teamName" class="form-label">NÁZEV TÝMU</label>
              <input
                type="text"
                class="form-control dark-input"
                id="teamName"
                v-model="teamName"
                required
                placeholder="Např. SK Slavoj"
              />
            </div>

            <div class="col-md-6 mb-3">
              <label for="teamLogo" class="form-label">LOGO TÝMU</label>
              <input
                type="file"
                class="form-control dark-input"
                id="teamLogo"
                @change="handleFileUpload"
                accept="image/*"
                ref="fileInput"
              />
            </div>
          </div>

          <div class="d-flex gap-2 mt-4">
            <button type="submit" class="btn-red-glow flex-grow-1">
              {{ editing ? "ULOŽIT ZMĚNY" : "PŘIDAT TÝM" }}
            </button>

            <button
              v-if="editing"
              type="button"
              class="btn btn-outline-secondary"
              @click="cancelEdit"
            >
              ZRUŠIT
            </button>
          </div>
        </form>

        <transition name="fade">
          <div v-if="successMessage" class="alert-box success mt-3">
            ✅ {{ successMessage }}
          </div>
        </transition>
        <transition name="fade">
          <div v-if="errorMessage" class="alert-box error mt-3">
            ⚠️ {{ errorMessage }}
          </div>
        </transition>
      </div>

      <h2 class="section-title text-center mb-4">SEZNAM TÝMŮ</h2>

      <div v-if="loading" class="text-center text-muted">
        <div class="spinner-border text-danger" role="status"></div>
      </div>

      <div v-else class="teams-grid">
        <div v-for="team in teams" :key="team.id" class="team-card">
          <div class="team-card-content">
            <img :src="team.logo" :alt="team.name" class="team-list-logo" />
            <h3 class="team-list-name">{{ team.name }}</h3>
          </div>

          <div class="team-actions">
            <button
              @click="editTeam(team)"
              class="action-btn edit"
              title="Upravit"
            >
              <svg
                class="icon-sm"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                ></path>
                <path
                  d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                ></path>
              </svg>
            </button>
            <button
              @click="deleteTeam(team.id, team.logo)"
              class="action-btn delete"
              title="Smazat"
            >
              <svg
                class="icon-sm"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

export default {
  data() {
    return {
      teamName: "",
      teamLogo: null,
      teams: [],
      loading: false,
      successMessage: "",
      errorMessage: "",
      editing: false,
      editId: null,
      existingLogo: null,
    };
  },
  async mounted() {
    await this.fetchTeams();
  },
  methods: {
    handleFileUpload(event) {
      this.teamLogo = event.target.files[0];
    },

    async fetchTeams() {
      this.loading = true;
      try {
        const teamsSnapshot = await getDocs(collection(db, "teams"));
        this.teams = teamsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error("❌ Chyba při načítání týmů:", error);
        this.errorMessage = "Nepodařilo se načíst týmy!";
      } finally {
        this.loading = false;
      }
    },

    async addTeam() {
      if (!this.teamName || !this.teamLogo) {
        this.errorMessage = "Vyplňte název týmu a nahrajte logo!";
        return;
      }

      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";

      try {
        const storageRef = ref(
          storage,
          `team-logos/${Date.now()}_${this.teamLogo.name}`
        );
        await uploadBytes(storageRef, this.teamLogo);
        const logoURL = await getDownloadURL(storageRef);

        await addDoc(collection(db, "teams"), {
          name: this.teamName,
          logo: logoURL,
        });

        this.successMessage = "Tým úspěšně přidán!";
        this.resetForm();
        await this.fetchTeams();
      } catch (error) {
        console.error("❌ Chyba při přidávání týmu:", error);
        this.errorMessage = "Chyba při ukládání týmu!";
      } finally {
        this.loading = false;
      }
    },

    editTeam(team) {
      this.teamName = team.name;
      this.existingLogo = team.logo;
      this.editing = true;
      this.editId = team.id;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    async updateTeam() {
      if (!this.teamName) {
        this.errorMessage = "Název týmu nesmí být prázdný!";
        return;
      }

      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";

      try {
        let logoURL = this.existingLogo;

        if (this.teamLogo) {
          const storageRef = ref(
            storage,
            `team-logos/${Date.now()}_${this.teamLogo.name}`
          );
          await uploadBytes(storageRef, this.teamLogo);
          logoURL = await getDownloadURL(storageRef);
        }

        await updateDoc(doc(db, "teams", this.editId), {
          name: this.teamName,
          logo: logoURL,
        });

        this.successMessage = "Tým úspěšně upraven!";
        this.cancelEdit();
        await this.fetchTeams();
      } catch (error) {
        console.error("❌ Chyba při úpravě týmu:", error);
        this.errorMessage = "Chyba při ukládání změn!";
      } finally {
        this.loading = false;
      }
    },

    async deleteTeam(teamId, logoUrl) {
      if (!confirm("Opravdu chcete tento tým smazat?")) return;

      try {
        await deleteDoc(doc(db, "teams", teamId));

        if (logoUrl) {
          try {
            const logoRef = ref(storage, logoUrl);
            await deleteObject(logoRef);
          } catch (e) {
            console.warn("Logo not found in storage");
          }
        }

        this.successMessage = "Tým úspěšně smazán!";
        await this.fetchTeams();
      } catch (error) {
        console.error("❌ Chyba při mazání týmu:", error);
        this.errorMessage = "Nepodařilo se smazat tým!";
      }
    },

    cancelEdit() {
      this.resetForm();
    },

    resetForm() {
      this.editing = false;
      this.editId = null;
      this.teamName = "";
      this.teamLogo = null;
      this.existingLogo = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
  },
};
</script>

<style scoped>
/* =========================================
   DARK THEME STYLING
   ========================================= */

.dark-theme-wrapper {
  background-color: #000000;
  min-height: 100vh;
  /* TOTO JE KLÍČOVÉ: Nastaví bílé písmo pro celou stránku */
  color: #ffffff;
  font-family: "Roboto", sans-serif;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #fff; /* Pojistka pro nadpis */
  text-transform: uppercase;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ccc;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* Karta formuláře */
.admin-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 15px;
  padding: 2.5rem;
  max-width: 800px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

/* Inputy */
.form-label {
  font-size: 0.8rem;
  font-weight: bold;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: block;
}

.dark-input {
  background-color: #000;
  border: 1px solid #444;
  color: #fff;
  padding: 10px;
  border-radius: 8px;
}

.dark-input:focus {
  border-color: #dc3545;
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
}

/* Tlačítka */
.btn-red-glow {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.btn-red-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.5);
}

.btn-outline-secondary {
  border: 1px solid #666;
  color: #ccc;
  background: transparent;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: bold;
}

.btn-outline-secondary:hover {
  background: #333;
  color: #fff;
}

/* Grid týmů */
.teams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  padding-bottom: 50px;
}

.team-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.2s;
  position: relative;
}

.team-card:hover {
  border-color: #555;
  transform: translateY(-3px);
}

.team-card-content {
  text-align: center;
  margin-bottom: 15px;
}

.team-list-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin-bottom: 10px;
}

.team-list-name {
  font-size: 1rem;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

/* Akce */
.team-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: #222;
  border: 1px solid #444;
  border-radius: 6px;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #aaa;
}

.action-btn:hover {
  background: #333;
  color: #fff;
}

.action-btn.delete:hover {
  background: #dc3545;
  border-color: #dc3545;
  color: white;
}

.icon-sm {
  width: 18px;
  height: 18px;
}

/* Alerty */
.alert-box {
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
}

.alert-box.success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid #28a745;
}

.alert-box.error {
  background: rgba(220, 53, 69, 0.1);
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
